/**
 * BIRHAUS Spanish Coverage Validator
 * 
 * Comprehensive testing utilities for BIRHAUS Principle #7: "Bilingual by design"
 * 
 * Features:
 * - Spanish-first label validation
 * - Bilingual completeness testing
 * - Financial terminology coverage
 * - Paraguay-specific language patterns
 * - Cultural context validation
 */

/* eslint-disable security/detect-object-injection, security/detect-non-literal-regexp, security/detect-unsafe-regex */
// Spanish language validation requires controlled object access and regex patterns

// import { screen, within } from '@testing-library/react' // Available for future test extensions

export interface SpanishCoverageViolation {
  type: 'missing-spanish' | 'missing-english' | 'invalid-pattern' | 'financial-term' | 'cultural-context'
  element: Element
  severity: 'minor' | 'moderate' | 'serious' | 'critical'
  message: string
  messageEs: string
  currentText?: string
  suggestedSpanish?: string
  suggestedEnglish?: string
  birhausPrinciple: number
  context: 'navigation' | 'form' | 'button' | 'content' | 'error' | 'financial'
}

export interface SpanishTestOptions {
  // Coverage requirements
  requireSpanishPrimary?: boolean
  requireEnglishFallback?: boolean
  strictFinancialTerms?: boolean
  
  // Language patterns
  validateParaguayanSpanish?: boolean
  validateFinancialVocabulary?: boolean
  validateCurrencyFormats?: boolean
  
  // Component coverage
  checkBirhausComponents?: boolean
  checkErrorMessages?: boolean
  checkButtonLabels?: boolean
  checkFormLabels?: boolean
  
  // Selectors
  birhausSelector?: string
  interactiveSelector?: string
  contentSelector?: string
}

export class SpanishCoverageValidator {
  private options: Required<SpanishTestOptions>
  
  // Paraguay-specific financial terminology
  private readonly financialTerms = {
    // Currency and amounts
    'amount': { es: 'monto', alternatives: ['cantidad', 'importe'] },
    'balance': { es: 'saldo', alternatives: ['balance'] },
    'income': { es: 'ingreso', alternatives: ['entrada'] },
    'expense': { es: 'gasto', alternatives: ['egreso'] },
    'transfer': { es: 'transferencia', alternatives: [] },
    'payment': { es: 'pago', alternatives: [] },
    'receipt': { es: 'recibo', alternatives: ['comprobante'] },
    'invoice': { es: 'factura', alternatives: [] },
    'tax': { es: 'impuesto', alternatives: [] },
    'total': { es: 'total', alternatives: [] },
    'subtotal': { es: 'subtotal', alternatives: [] },
    
    // Status terms
    'pending': { es: 'pendiente', alternatives: ['en espera'] },
    'approved': { es: 'aprobado', alternatives: ['autorizado'] },
    'rejected': { es: 'rechazado', alternatives: ['denegado'] },
    'completed': { es: 'completado', alternatives: ['finalizado'] },
    'cancelled': { es: 'cancelado', alternatives: ['anulado'] },
    
    // Actions
    'save': { es: 'guardar', alternatives: [] },
    'cancel': { es: 'cancelar', alternatives: [] },
    'edit': { es: 'editar', alternatives: ['modificar'] },
    'delete': { es: 'eliminar', alternatives: ['borrar'] },
    'approve': { es: 'aprobar', alternatives: ['autorizar'] },
    'reject': { es: 'rechazar', alternatives: ['denegar'] },
    'export': { es: 'exportar', alternatives: [] },
    'print': { es: 'imprimir', alternatives: [] },
    
    // Church-specific terms
    'donation': { es: 'donación', alternatives: ['ofrenda'] },
    'tithe': { es: 'diezmo', alternatives: [] },
    'offering': { es: 'ofrenda', alternatives: [] },
    'church': { es: 'iglesia', alternatives: [] },
    'pastor': { es: 'pastor', alternatives: [] },
    'member': { es: 'miembro', alternatives: [] },
    'treasurer': { es: 'tesorero', alternatives: [] },
    'administrator': { es: 'administrador', alternatives: [] }
  }
  
  // Common Spanish patterns for Paraguay
  private readonly spanishPatterns = [
    /[ñáéíóúü]/i, // Spanish characters
    /ción\b/i,    // -ción endings
    /dad\b/i,     // -dad endings
    /mente\b/i,   // -mente adverbs
    /\bel\b|\bla\b|\blos\b|\blas\b/i, // Articles
    /\ben\b|\bde\b|\bdel\b|\bal\b/i,  // Prepositions
    /\by\b|\bo\b|\bpero\b|\bsi\b/i    // Conjunctions
  ]
  
  constructor(options: SpanishTestOptions = {}) {
    this.options = {
      requireSpanishPrimary: true,
      requireEnglishFallback: true,
      strictFinancialTerms: true,
      validateParaguayanSpanish: true,
      validateFinancialVocabulary: true,
      validateCurrencyFormats: true,
      checkBirhausComponents: true,
      checkErrorMessages: true,
      checkButtonLabels: true,
      checkFormLabels: true,
      birhausSelector: '[data-birhaus-component]',
      interactiveSelector: 'button, a, input, select, textarea, [role="button"]',
      contentSelector: 'h1, h2, h3, h4, h5, h6, p, span, div[class*="text"], label',
      ...options
    }
  }
  
  /**
   * Validate all Spanish coverage requirements
   */
  validateAll(container: HTMLElement = document.body): SpanishCoverageViolation[] {
    const violations: SpanishCoverageViolation[] = []
    
    if (this.options.checkBirhausComponents) {
      violations.push(...this.validateBirhausComponents(container))
    }
    
    if (this.options.checkButtonLabels) {
      violations.push(...this.validateButtonLabels(container))
    }
    
    if (this.options.checkFormLabels) {
      violations.push(...this.validateFormLabels(container))
    }
    
    if (this.options.checkErrorMessages) {
      violations.push(...this.validateErrorMessages(container))
    }
    
    if (this.options.validateFinancialVocabulary) {
      violations.push(...this.validateFinancialTerminology(container))
    }
    
    if (this.options.validateCurrencyFormats) {
      violations.push(...this.validateCurrencyFormats(container))
    }
    
    return violations
  }
  
  /**
   * Validate BIRHAUS components have Spanish-first labels
   */
  validateBirhausComponents(container: HTMLElement): SpanishCoverageViolation[] {
    const violations: SpanishCoverageViolation[] = []
    const birhausComponents = container.querySelectorAll(this.options.birhausSelector)
    
    birhausComponents.forEach(component => {
      const componentType = component.getAttribute('data-birhaus-component')
      const hasSpanishLabel = this.checkSpanishLabels(component)
      const hasEnglishFallback = this.checkEnglishFallbacks(component)
      
      if (this.options.requireSpanishPrimary && !hasSpanishLabel) {
        violations.push({
          type: 'missing-spanish',
          element: component,
          severity: 'serious',
          message: `BIRHAUS ${componentType} component missing Spanish labels`,
          messageEs: `Componente BIRHAUS ${componentType} carece de etiquetas en español`,
          suggestedSpanish: this.suggestSpanishLabel(component),
          birhausPrinciple: 7,
          context: this.getElementContext(component)
        })
      }
      
      if (this.options.requireEnglishFallback && hasSpanishLabel && !hasEnglishFallback) {
        violations.push({
          type: 'missing-english',
          element: component,
          severity: 'moderate',
          message: `BIRHAUS ${componentType} component missing English fallback labels`,
          messageEs: `Componente BIRHAUS ${componentType} carece de etiquetas de respaldo en inglés`,
          suggestedEnglish: this.suggestEnglishLabel(component),
          birhausPrinciple: 7,
          context: this.getElementContext(component)
        })
      }
    })
    
    return violations
  }
  
  /**
   * Validate button labels are in Spanish-first
   */
  validateButtonLabels(container: HTMLElement): SpanishCoverageViolation[] {
    const violations: SpanishCoverageViolation[] = []
    const buttons = container.querySelectorAll('button, [role="button"], input[type="submit"], input[type="button"]')
    
    buttons.forEach(button => {
      const text = this.getElementText(button)
      const hasSpanish = this.isSpanishText(text)
      
      if (!hasSpanish && text.length > 0) {
        const suggestedSpanish = this.translateButtonText(text)
        
        violations.push({
          type: 'missing-spanish',
          element: button,
          severity: 'moderate',
          message: `Button text "${text}" should be in Spanish`,
          messageEs: `Texto del botón "${text}" debería estar en español`,
          currentText: text,
          suggestedSpanish,
          birhausPrinciple: 7,
          context: 'button'
        })
      }
    })
    
    return violations
  }
  
  /**
   * Validate form labels and placeholders
   */
  validateFormLabels(container: HTMLElement): SpanishCoverageViolation[] {
    const violations: SpanishCoverageViolation[] = []
    const formElements = container.querySelectorAll('input, select, textarea')
    
    formElements.forEach(element => {
      // Check labels
      const label = this.getAssociatedLabel(element)
      if (label) {
        const labelText = label.textContent?.trim() || ''
        if (!this.isSpanishText(labelText) && labelText.length > 0) {
          violations.push({
            type: 'missing-spanish',
            element: label,
            severity: 'moderate',
            message: `Form label "${labelText}" should be in Spanish`,
            messageEs: `Etiqueta de formulario "${labelText}" debería estar en español`,
            currentText: labelText,
            suggestedSpanish: this.translateFormLabel(labelText),
            birhausPrinciple: 7,
            context: 'form'
          })
        }
      }
      
      // Check placeholders
      const placeholder = element.getAttribute('placeholder')
      if (placeholder && !this.isSpanishText(placeholder)) {
        violations.push({
          type: 'missing-spanish',
          element: element,
          severity: 'minor',
          message: `Placeholder "${placeholder}" should be in Spanish`,
          messageEs: `Marcador de posición "${placeholder}" debería estar en español`,
          currentText: placeholder,
          suggestedSpanish: this.translatePlaceholder(placeholder),
          birhausPrinciple: 7,
          context: 'form'
        })
      }
    })
    
    return violations
  }
  
  /**
   * Validate error messages are in Spanish with "Qué pasó + cómo resolver" pattern
   */
  validateErrorMessages(container: HTMLElement): SpanishCoverageViolation[] {
    const violations: SpanishCoverageViolation[] = []
    const errorElements = container.querySelectorAll(
      '.error, .alert-error, [role="alert"], [class*="error"], [data-error], [aria-invalid="true"] + *'
    )
    
    errorElements.forEach(element => {
      const errorText = element.textContent?.trim() || ''
      
      if (errorText.length > 0) {
        const hasSpanish = this.isSpanishText(errorText)
        const hasProperPattern = this.hasProperErrorPattern(errorText)
        
        if (!hasSpanish) {
          violations.push({
            type: 'missing-spanish',
            element: element,
            severity: 'serious',
            message: `Error message "${errorText}" should be in Spanish`,
            messageEs: `Mensaje de error "${errorText}" debería estar en español`,
            currentText: errorText,
            suggestedSpanish: this.translateErrorMessage(errorText),
            birhausPrinciple: 7,
            context: 'error'
          })
        } else if (!hasProperPattern) {
          violations.push({
            type: 'invalid-pattern',
            element: element,
            severity: 'moderate',
            message: `Error message should follow "Qué pasó + cómo resolver" pattern`,
            messageEs: `Mensaje de error debería seguir el patrón "Qué pasó + cómo resolver"`,
            currentText: errorText,
            suggestedSpanish: this.improveErrorPattern(errorText),
            birhausPrinciple: 7,
            context: 'error'
          })
        }
      }
    })
    
    return violations
  }
  
  /**
   * Validate financial terminology is properly translated
   */
  validateFinancialTerminology(container: HTMLElement): SpanishCoverageViolation[] {
    const violations: SpanishCoverageViolation[] = []
    const textElements = container.querySelectorAll(this.options.contentSelector)
    
    textElements.forEach(element => {
      const text = element.textContent?.toLowerCase() || ''
      
      // Check for English financial terms that should be translated
      Object.entries(this.financialTerms).forEach(([englishTerm, spanishData]) => {
        const regex = new RegExp(`\\b${englishTerm}\\b`, 'gi')
        if (regex.test(text)) {
          violations.push({
            type: 'financial-term',
            element: element,
            severity: this.options.strictFinancialTerms ? 'serious' : 'moderate',
            message: `Financial term "${englishTerm}" should use Spanish "${spanishData.es}"`,
            messageEs: `Término financiero "${englishTerm}" debería usar español "${spanishData.es}"`,
            currentText: englishTerm,
            suggestedSpanish: spanishData.es,
            birhausPrinciple: 7,
            context: 'financial'
          })
        }
      })
    })
    
    return violations
  }
  
  /**
   * Validate currency formats follow Paraguay conventions
   */
  validateCurrencyFormats(container: HTMLElement): SpanishCoverageViolation[] {
    const violations: SpanishCoverageViolation[] = []
    const textElements = container.querySelectorAll(this.options.contentSelector)
    
    textElements.forEach(element => {
      const text = element.textContent || ''
      
      // Look for currency patterns
      const currencyPatterns = [
        /\$[\d,]+/g,           // Dollar signs
        /USD\s*[\d,]+/g,       // USD prefix
        /[\d,]+\s*USD/g,       // USD suffix
        /₲[\d,]+/g,            // Guaraní symbol
        /PYG\s*[\d,]+/g,       // PYG prefix
        /[\d,]+\s*PYG/g        // PYG suffix
      ]
      
      currencyPatterns.forEach(pattern => {
        const matches = text.match(pattern)
        if (matches) {
          matches.forEach(match => {
            if (!this.isValidParaguayanCurrency(match)) {
              violations.push({
                type: 'invalid-pattern',
                element: element,
                severity: 'moderate',
                message: `Currency format "${match}" should follow Paraguay conventions`,
                messageEs: `Formato de moneda "${match}" debería seguir convenciones de Paraguay`,
                currentText: match,
                suggestedSpanish: this.formatAsParaguayanCurrency(match),
                birhausPrinciple: 7,
                context: 'financial'
              })
            }
          })
        }
      })
    })
    
    return violations
  }
  
  // Helper methods
  
  private checkSpanishLabels(element: Element): boolean {
    const spanishAttributes = [
      'data-label-es',
      'labelEs',
      'aria-label',
      'title'
    ]
    
    return spanishAttributes.some(attr => {
      const value = element.getAttribute(attr)
      return value && this.isSpanishText(value)
    })
  }
  
  private checkEnglishFallbacks(element: Element): boolean {
    const englishAttributes = [
      'data-label-en',
      'labelEn'
    ]
    
    return englishAttributes.some(attr => {
      const value = element.getAttribute(attr)
      return value && value.length > 0
    })
  }
  
  private isSpanishText(text: string): boolean {
    if (!text || text.length === 0) return false
    
    // Check for Spanish patterns
    return this.spanishPatterns.some(pattern => pattern.test(text)) ||
           // Check for Spanish financial terms
           Object.values(this.financialTerms).some(term => 
             text.toLowerCase().includes(term.es) ||
             term.alternatives.some(alt => text.toLowerCase().includes(alt))
           )
  }
  
  private getElementText(element: Element): string {
    return element.textContent?.trim() || 
           element.getAttribute('aria-label') || 
           element.getAttribute('title') || 
           element.getAttribute('alt') || ''
  }
  
  private getElementContext(element: Element): SpanishCoverageViolation['context'] {
    const tagName = element.tagName.toLowerCase()
    const role = element.getAttribute('role')
    const className = element.className
    
    if (tagName === 'button' || role === 'button') return 'button'
    if (tagName === 'nav' || role === 'navigation') return 'navigation'
    if (['input', 'select', 'textarea'].includes(tagName)) return 'form'
    if (className.includes('error') || element.getAttribute('role') === 'alert') return 'error'
    if (this.isFinancialElement(element)) return 'financial'
    
    return 'content'
  }
  
  private isFinancialElement(element: Element): boolean {
    const text = this.getElementText(element).toLowerCase()
    const className = element.className.toLowerCase()
    
    return Object.keys(this.financialTerms).some(term => 
      text.includes(term) || className.includes(term)
    )
  }
  
  private suggestSpanishLabel(element: Element): string {
    const componentType = element.getAttribute('data-birhaus-component')
    const currentText = this.getElementText(element)
    
    if (currentText) {
      return this.translateButtonText(currentText)
    }
    
    // Default suggestions based on component type
    const suggestions: Record<string, string> = {
      'button': 'Botón',
      'input': 'Campo de entrada',
      'select': 'Lista desplegable',
      'card': 'Tarjeta',
      'dialog': 'Diálogo',
      'table': 'Tabla',
      'tabs': 'Pestañas',
      'checkbox': 'Casilla de verificación'
    }
    
    return suggestions[componentType || ''] || 'Elemento'
  }
  
  private suggestEnglishLabel(element: Element): string {
    const spanishText = this.getElementText(element)
    return this.translateToEnglish(spanishText)
  }
  
  private translateButtonText(text: string): string {
    const translations: Record<string, string> = {
      'save': 'Guardar',
      'cancel': 'Cancelar',
      'edit': 'Editar',
      'delete': 'Eliminar',
      'submit': 'Enviar',
      'reset': 'Restablecer',
      'close': 'Cerrar',
      'open': 'Abrir',
      'add': 'Agregar',
      'remove': 'Quitar',
      'approve': 'Aprobar',
      'reject': 'Rechazar',
      'export': 'Exportar',
      'import': 'Importar',
      'print': 'Imprimir',
      'search': 'Buscar',
      'filter': 'Filtrar',
      'sort': 'Ordenar'
    }
    
    const lowerText = text.toLowerCase()
    return translations[lowerText] || text
  }
  
  private translateFormLabel(text: string): string {
    const translations: Record<string, string> = {
      'name': 'Nombre',
      'email': 'Correo electrónico',
      'phone': 'Teléfono',
      'address': 'Dirección',
      'amount': 'Monto',
      'date': 'Fecha',
      'description': 'Descripción',
      'category': 'Categoría',
      'status': 'Estado',
      'type': 'Tipo',
      'notes': 'Notas',
      'comments': 'Comentarios'
    }
    
    const lowerText = text.toLowerCase()
    return translations[lowerText] || text
  }
  
  private translatePlaceholder(text: string): string {
    const translations: Record<string, string> = {
      'enter name': 'Ingrese el nombre',
      'enter email': 'Ingrese el correo electrónico',
      'enter amount': 'Ingrese el monto',
      'select date': 'Seleccione la fecha',
      'search...': 'Buscar...',
      'type here': 'Escriba aquí',
      'optional': 'Opcional'
    }
    
    const lowerText = text.toLowerCase()
    return translations[lowerText] || text
  }
  
  private translateErrorMessage(text: string): string {
    const translations: Record<string, string> = {
      'required field': 'Campo obligatorio. Complete este campo para continuar.',
      'invalid email': 'Correo electrónico inválido. Verifique el formato (ejemplo@dominio.com).',
      'invalid amount': 'Monto inválido. Ingrese un número válido mayor a cero.',
      'network error': 'Error de conexión. Verifique su conexión a internet e intente nuevamente.',
      'server error': 'Error del servidor. El problema será resuelto pronto, intente más tarde.'
    }
    
    const lowerText = text.toLowerCase()
    return translations[lowerText] || `Error: ${text}. Verifique los datos e intente nuevamente.`
  }
  
  private translateToEnglish(spanishText: string): string {
    // Reverse translation for English fallbacks
    const reverseTranslations: Record<string, string> = {
      'guardar': 'Save',
      'cancelar': 'Cancel',
      'editar': 'Edit',
      'eliminar': 'Delete',
      'enviar': 'Submit',
      'cerrar': 'Close',
      'abrir': 'Open',
      'agregar': 'Add',
      'quitar': 'Remove'
    }
    
    const lowerText = spanishText.toLowerCase()
    return reverseTranslations[lowerText] || spanishText
  }
  
  private hasProperErrorPattern(text: string): boolean {
    // Check if error follows "Qué pasó + cómo resolver" pattern
    const hasExplanation = /error|problema|fallo|inválido|incorrecto/i.test(text)
    const hasSolution = /verifique|intente|complete|corrija|revise/i.test(text)
    
    return hasExplanation && hasSolution
  }
  
  private improveErrorPattern(text: string): string {
    // Add solution suggestion to error message
    const solutions = [
      'Verifique los datos e intente nuevamente.',
      'Complete la información requerida.',
      'Revise el formato e intente otra vez.',
      'Intente más tarde o contacte soporte.'
    ]
    
    const randomSolution = solutions[Math.floor(Math.random() * solutions.length)]
    return `${text} ${randomSolution}`
  }
  
  private getAssociatedLabel(element: Element): Element | null {
    const labelledBy = element.getAttribute('aria-labelledby')
    if (labelledBy) {
      return document.getElementById(labelledBy)
    }
    
    const id = element.getAttribute('id')
    if (id) {
      return document.querySelector(`label[for="${id}"]`)
    }
    
    return element.closest('label')
  }
  
  private isValidParaguayanCurrency(currencyText: string): boolean {
    // Check if follows Paraguay format: ₲ 123.456 or PYG 123.456
    const paraguayanPatterns = [
      /^₲\s?\d{1,3}(\.\d{3})*$/,
      /^PYG\s\d{1,3}(\.\d{3})*$/,
      /^\d{1,3}(\.\d{3})*\sPYG$/
    ]
    
    return paraguayanPatterns.some(pattern => pattern.test(currencyText))
  }
  
  private formatAsParaguayanCurrency(currencyText: string): string {
    // Extract number and format as Paraguayan Guaraní
    const number = currencyText.replace(/[^\d]/g, '')
    const formatted = new Intl.NumberFormat('es-PY').format(parseInt(number))
    return `₲ ${formatted}`
  }
}

/**
 * Test helper functions for Spanish coverage assertions
 */

/**
 * Assert Spanish-first compliance
 */
export function expectSpanishFirst(
  container?: HTMLElement,
  strict: boolean = true
): void {
  const validator = new SpanishCoverageValidator({ 
    requireSpanishPrimary: true,
    strictFinancialTerms: strict
  })
  const violations = validator.validateAll(container)
  
  const seriousViolations = violations.filter(v => v.severity === 'serious' || v.severity === 'critical')
  
  if (seriousViolations.length > 0) {
    const errorMessages = seriousViolations
      .map(v => v.messageEs)
      .join('\n')
    
    throw new Error(
      `Spanish-first violations detected:\n${errorMessages}\n\n` +
      `Total violations: ${violations.length} (${seriousViolations.length} serious)`
    )
  }
}

/**
 * Assert bilingual completeness
 */
export function expectBilingualCompleteness(
  container?: HTMLElement
): void {
  const validator = new SpanishCoverageValidator({ 
    requireSpanishPrimary: true,
    requireEnglishFallback: true
  })
  const violations = validator.validateAll(container)
  
  if (violations.length > 0) {
    const errorMessages = violations
      .map(v => v.message)
      .join('\n')
    
    throw new Error(
      `Bilingual completeness violations detected:\n${errorMessages}`
    )
  }
}

/**
 * Assert financial terminology is properly translated
 */
export function expectFinancialSpanish(
  container?: HTMLElement
): void {
  const validator = new SpanishCoverageValidator({ 
    validateFinancialVocabulary: true,
    strictFinancialTerms: true
  })
  const violations = validator.validateFinancialTerminology(container || document.body)
  
  if (violations.length > 0) {
    const errorMessages = violations
      .map(v => `${v.currentText} → ${v.suggestedSpanish}`)
      .join('\n')
    
    throw new Error(
      `Financial terminology violations detected:\n${errorMessages}`
    )
  }
}

